import { useState, type FormEvent } from "react";
import { useAction, useConvex, useMutation, useQuery } from "convex/react";
import { CheckCircle2, Database, Download, ExternalLink } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { Badge, Button, Card, ConfirmDialog, Field, Input, Notice, PageHeader } from "../components/ui";
import { AlertSoundSettings } from "../components/AlertSoundSettings";
import { CATEGORY_LABELS, errorMessage, formatDateCompact, formatNumber } from "../lib/format";


type SheetPreview = { name: string; category: keyof typeof CATEGORY_LABELS; rows: number; identifiableRows: number };
type MigrationReport =
  | { kind: "preview"; data: { totalRows: number; sheets: SheetPreview[] } }
  | { kind: "import"; data: { inserted: number; duplicates: number; invalid: number } };

/** Rend le résultat d'une migration lisible, au lieu d'un bloc JSON brut. */
function MigrationReportView({ report }: { report: MigrationReport }) {
  if (report.kind === "preview") {
    return (
      <div className="report">
        <div className="report__head">
          <CheckCircle2 size={18} aria-hidden="true" />
          <span>Prévisualisation · {formatNumber(report.data.totalRows)} lignes lues dans Google Sheets. Rien n’a encore été importé.</span>
        </div>
        <div className="report__grid">
          {report.data.sheets.map((sheet) => (
            <div key={sheet.name} className="report__cell">
              <span>{CATEGORY_LABELS[sheet.category] ?? sheet.name}</span>
              <strong>{formatNumber(sheet.identifiableRows)}</strong>
              <small>{sheet.rows === sheet.identifiableRows ? "toutes exploitables" : `sur ${formatNumber(sheet.rows)} lignes`}</small>
            </div>
          ))}
        </div>
      </div>
    );
  }
  const { inserted, duplicates, invalid } = report.data;
  return (
    <div className="report report--success">
      <div className="report__head">
        <CheckCircle2 size={18} aria-hidden="true" />
        <span>Import terminé · {formatNumber(inserted)} entrées ajoutées à la blacklist.</span>
      </div>
      <div className="report__grid">
        <div className="report__cell"><span>Ajoutées</span><strong>{formatNumber(inserted)}</strong><small>nouvelles sanctions</small></div>
        <div className="report__cell"><span>Déjà connues</span><strong>{formatNumber(duplicates)}</strong><small>fusionnées, sans doublon</small></div>
        <div className="report__cell"><span>Non exploitables</span><strong>{formatNumber(invalid)}</strong><small>{invalid === 0 ? "aucune perte" : "lignes sans identifiant"}</small></div>
      </div>
    </div>
  );
}

type Categorie = keyof typeof CATEGORY_LABELS;

/** Échappement CSV : guillemets doublés, et champ encadré dès qu'il contient un séparateur. */
function champCsv(valeur: string): string {
  return /[",;\r\n]/.test(valeur) ? `"${valeur.replace(/"/g, '""')}"` : valeur;
}

/**
 * Export d'une catégorie, sanctions actives uniquement. Le fichier est construit dans le
 * navigateur à partir des données déjà renvoyées par le serveur : rien ne transite ailleurs.
 */
function ExportCard() {
  const convex = useConvex();
  const [enCours, setEnCours] = useState<Categorie | "all" | null>(null);
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);

  /** Fabrique commune : échappement, BOM et téléchargement sont identiques pour les deux exports. */
  const telecharger = (entetes: string[], corps: string[][], nom: string) => {
    const lignes = [entetes, ...corps].map((ligne) => ligne.map((valeur) => champCsv(String(valeur))).join(","));
    const contenu = `\uFEFF${lignes.join("\r\n")}`;
    const url = URL.createObjectURL(new Blob([contenu], { type: "text/csv;charset=utf-8" }));
    const lien = document.createElement("a");
    lien.href = url;
    lien.download = `${nom}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    URL.revokeObjectURL(url);
  };

  /** Toutes catégories dans un fichier unique, avec la catégorie et l'origine en colonnes. */
  const exporterTout = async () => {
    setEnCours("all"); setMessage(null);
    try {
      const lignes = await convex.query(api.blacklist.exportAll, {});
      if (lignes.length === 0) { setMessage({ tone: "error", text: "Aucune sanction active à exporter." }); return; }
      telecharger(
        ["Catégorie", "Epic ID", "Pseudo Epic", "Discord ID", "Pseudo Discord", "Serveurs", "Raison", "Ajouté le", "Origine", "Contournement", "Comptes liés"],
        lignes.map((l) => [
          CATEGORY_LABELS[l.category as Categorie], l.epicId, l.epicPseudo, l.discordId, l.discordPseudo,
          l.serversBan, l.reason, formatDateCompact(l.addedAt),
          l.source === "migration" ? "import" : l.source === "suspect" ? "détection" : "manuel",
          l.evasion ? "oui" : "non", String(l.linkedAccounts),
        ]),
        "blacklist-complete",
      );
      setMessage({ tone: "success", text: `${lignes.length} entrées exportées, toutes catégories confondues.` });
    } catch (reason) {
      setMessage({ tone: "error", text: errorMessage(reason) });
    } finally { setEnCours(null); }
  };

  const exporter = async (categorie: Categorie) => {
    setEnCours(categorie); setMessage(null);
    try {
      const lignes = await convex.query(api.blacklist.exportCategory, { category: categorie });
      if (lignes.length === 0) {
        setMessage({ tone: "error", text: `Aucune sanction active dans « ${CATEGORY_LABELS[categorie]} ».` });
        return;
      }
      const entetes = ["Epic ID", "Pseudo Epic", "Discord ID", "Pseudo Discord", "Serveurs", "Raison", "Ajouté le", "Contournement", "Comptes liés"];
      const corps = lignes.map((ligne) => [
        ligne.epicId, ligne.epicPseudo, ligne.discordId, ligne.discordPseudo,
        ligne.serversBan, ligne.reason, formatDateCompact(ligne.addedAt),
        ligne.evasion ? "oui" : "non", String(ligne.linkedAccounts),
      ].map((valeur) => champCsv(String(valeur))).join(","));
      // Le BOM force Excel à lire le fichier en UTF-8 : sans lui, les accents ressortent cassés.
      const contenu = `\uFEFF${[entetes.join(","), ...corps].join("\r\n")}`;
      const url = URL.createObjectURL(new Blob([contenu], { type: "text/csv;charset=utf-8" }));
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = `blacklist-${categorie}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      URL.revokeObjectURL(url);
      setMessage({ tone: "success", text: `${lignes.length} entrée${lignes.length > 1 ? "s" : ""} exportée${lignes.length > 1 ? "s" : ""}.` });
    } catch (reason) {
      setMessage({ tone: "error", text: errorMessage(reason) });
    } finally { setEnCours(null); }
  };

  return (
    <Card>
      <div className="card-heading"><h2>Export CSV</h2></div>
      <p className="muted">
        Une catégorie par fichier, toutes les colonnes, <strong>sanctions actives uniquement</strong> —
        les sanctions levées sont écartées. Encodage UTF-8 lisible par Excel.
      </p>
      <div className="button-row button-row--wrap">
        {(Object.keys(CATEGORY_LABELS) as Categorie[]).map((categorie) => (
          <Button key={categorie} variant="secondary" loading={enCours === categorie}
            onClick={() => void exporter(categorie)}>
            <Download size={15} /> {CATEGORY_LABELS[categorie]}
          </Button>
        ))}
        <Button loading={enCours === "all"} onClick={() => void exporterTout()}>
          <Download size={15} /> Tout exporter
        </Button>
      </div>
      {message && <Notice tone={message.tone}>{message.text}</Notice>}
    </Card>
  );
}

/** Bot Discord des alertes en DM ; les destinataires se choisissent sur chaque surveillance. */
function AlertDmCard({ botConfigured }: { botConfigured: boolean }) {
  const setBotToken = useAction(api.settings.setDiscordBotToken);
  const testDm = useAction(api.settings.testAlertDm);
  const [busy, setBusy] = useState<"token" | "test" | null>(null);
  const [message, setMessage] = useState<{ tone: "error" | "success" | "warning"; text: string } | null>(null);

  const saveToken = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    setBusy("token"); setMessage(null);
    try {
      const { botName } = await setBotToken({ token: String(new FormData(formElement).get("botToken") ?? "") });
      formElement.reset();
      setMessage({ tone: "success", text: `Bot « ${botName} » connecté.` });
    } catch (reason) { setMessage({ tone: "error", text: errorMessage(reason) }); }
    finally { setBusy(null); }
  };

  const runTest = async () => {
    setBusy("test"); setMessage(null);
    try {
      const result = await testDm({});
      setMessage(result.ok
        ? { tone: "success", text: "DM de test envoyé : vérifiez vos messages privés Discord." }
        : { tone: "warning", text: result.error ?? "Le DM n’a pas pu être envoyé." });
    } catch (reason) { setMessage({ tone: "error", text: errorMessage(reason) }); }
    finally { setBusy(null); }
  };

  return (
    <Card>
      <div className="card-heading"><h2>Bot Discord des DM</h2></div>
      <p className="muted">
        Les administrateurs à prévenir se choisissent sur chaque surveillance. Le bot doit partager un serveur
        Discord avec eux, et leurs DM doivent être ouverts.
      </p>
      <div className="token-status"><span>Bot Discord</span><Badge tone={botConfigured ? "success" : "warning"}>{botConfigured ? "Configuré" : "À configurer"}</Badge></div>
      <form onSubmit={saveToken} className="form-stack">
        <Field label="Jeton du bot" hint="Discord Developer Portal → votre application → Bot → Reset Token. Il est chiffré côté serveur.">
          <Input name="botToken" type="password" autoComplete="off" required minLength={50} placeholder={botConfigured ? "Remplacer le jeton actuel" : "Jeton du bot Discord"} />
        </Field>
        <div className="button-row">
          <Button type="submit" variant="secondary" loading={busy === "token"}>Vérifier et enregistrer</Button>
          <Button variant="secondary" loading={busy === "test"} disabled={!botConfigured} onClick={() => void runTest()}>M’envoyer un DM de test</Button>
        </div>
      </form>
      {message && <Notice tone={message.tone}>{message.text}</Notice>}
    </Card>
  );
}

export function SettingsPage() {
  const preview = useAction(api.migration.previewSheets);
  const migrate = useAction(api.migration.runSheetsMigration);
  const setWlsToken = useAction(api.settings.setWlsToken);
  const settings = useQuery(api.settings.list);
  const [loading, setLoading] = useState<"preview" | "run" | "token" | "webhook" | null>(null);
  const [report, setReport] = useState<MigrationReport | null>(null);
  const [confirmImport, setConfirmImport] = useState(false);
  const [error, setError] = useState("");
  const [tokenSaved, setTokenSaved] = useState(false);
  const [webhookSaved, setWebhookSaved] = useState(false);
  const setDiscordWebhook = useMutation(api.settings.setDiscordWebhook);

  const saveToken = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const token = String(new FormData(formElement).get("token") ?? "");
    setLoading("token"); setError(""); setTokenSaved(false);
    try {
      await setWlsToken({ token });
      formElement.reset();
      setTokenSaved(true);
    } catch (reason) { setError(errorMessage(reason)); }
    finally { setLoading(null); }
  };

  const saveWebhook = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const url = String(new FormData(formElement).get("webhook") ?? "");
    setLoading("webhook"); setError(""); setWebhookSaved(false);
    try {
      await setDiscordWebhook({ url });
      formElement.reset();
      setWebhookSaved(true);
    } catch (reason) { setError(errorMessage(reason)); }
    finally { setLoading(null); }
  };

  const runPreview = async () => {
    setLoading("preview"); setError(""); setReport(null);
    try { setReport({ kind: "preview", data: await preview({}) }); }
    catch (reason) { setError(errorMessage(reason)); }
    finally { setLoading(null); }
  };
  const runMigration = async () => {
    setLoading("run"); setError(""); setReport(null);
    try { setReport({ kind: "import", data: await migrate({}) }); }
    catch (reason) { setError(errorMessage(reason)); }
    finally { setLoading(null); setConfirmImport(false); }
  };
  return <>
    <PageHeader title="Paramètres & données" description="État des intégrations, migration initiale et documentation de l’API privée." />
    <div className="settings-grid">
      <ExportCard />
      <Card>
        <div className="card-heading"><h2>Accès WLS authentifié</h2></div>
        <p className="muted">
          Le contrôle lit toutes les pages publiques. Ce Bearer autorise aussi le serveur à vérifier chaque minute les
          groupes privés via <code>accounts.wls.gg</code>. Il est chiffré côté serveur et n’est jamais renvoyé au navigateur.
        </p>
        <div className="token-status"><span>État</span><Badge tone={settings?.wlsTokenConfigured ? "success" : "warning"}>{settings?.wlsTokenConfigured ? "Configuré" : "Groupes privés désactivés"}</Badge></div>
        <form onSubmit={saveToken} className="form-stack">
          <Field label="Jeton OAuth WLS" hint="Collez le jeton Bearer, pas les cookies de votre navigateur.">
            <Input name="token" type="password" autoComplete="off" minLength={20} required placeholder="Bearer token accounts.wls.gg" />
          </Field>
          <Button type="submit" loading={loading === "token"}>Enregistrer de manière chiffrée</Button>
        </form>
        {tokenSaved && <Notice tone="success">Jeton WLS enregistré.</Notice>}
      </Card>
      <Card>
        <div className="card-heading"><h2>Google Sheets WLS</h2></div>
        <p className="muted">Trois feuilles sources sont associées aux catégories Cheat, Team up et Insultes admins. Les lignes sont normalisées et dédupliquées.</p>
        <div className="button-row"><Button variant="secondary" loading={loading === "preview"} onClick={() => void runPreview()}>Prévisualiser</Button><Button loading={loading === "run"} onClick={() => setConfirmImport(true)}><Database size={16} /> Importer</Button></div>
        {report && <MigrationReportView report={report} />}
      </Card>
      <Card>
        <div className="card-heading"><h2>Notifications Discord</h2></div>
        <p className="muted">Les alertes de surveillance de tournoi sont envoyées sur ce salon. Elles restent également visibles dans l’application.</p>
        <div className="token-status"><span>État</span><Badge tone={settings?.discordWebhookConfigured ? "success" : "warning"}>{settings?.discordWebhookConfigured ? "Configuré" : "À configurer"}</Badge></div>
        <form onSubmit={saveWebhook} className="form-stack">
          <Field label="Webhook Discord" hint="Laissez vide pour désactiver les notifications.">
            <Input name="webhook" type="url" autoComplete="off" placeholder="https://discord.com/api/webhooks/…" />
          </Field>
          <Button type="submit" loading={loading === "webhook"}>Enregistrer le webhook</Button>
        </form>
        {webhookSaved && <Notice tone="success">Webhook Discord enregistré.</Notice>}
      </Card>
      <AlertDmCard botConfigured={settings?.discordBotConfigured ?? false} />
      <Card>
        <div className="card-heading"><h2>Son des alertes</h2></div>
        <p className="muted">Réglage propre à ce navigateur. Cliquez une fois dans l’application après son ouverture : les navigateurs bloquent le son tant qu’aucune interaction n’a eu lieu.</p>
        <AlertSoundSettings />
      </Card>
      <Card>
        <div className="card-heading"><h2>API privée</h2></div>
        <div className="api-doc"><div><code>GET /v1/check?epic_id=…</code><span>Vérifier Epic ID, Discord ID ou pseudo (POST JSON également accepté).</span></div><div><code>GET /v1/blacklist</code><span>Exporter la liste active avec la permission dédiée.</span></div><div><code>x-api-key: wls_bl_…</code><span>Une clé par intégration, révocable à tout moment.</span></div></div>
        <a className="text-link" href="https://www.wls.gg" target="_blank" rel="noreferrer">Ouvrir WLS <ExternalLink size={14} /></a>
      </Card>
    </div>
    {error && <Notice tone="error">{error}</Notice>}
    {confirmImport && (
      <ConfirmDialog
        title="Importer les feuilles Google Sheets"
        description="Les trois feuilles seront lues et ajoutées à la blacklist. Les entrées déjà connues sont fusionnées : relancer l’import ne crée pas de doublon."
        target="Cheaters · Team Up · Insultes Admins"
        confirmLabel="Lancer l’import"
        tone="primary"
        onConfirm={runMigration}
        onClose={() => setConfirmImport(false)}
      />
    )}
  </>;
}
